import React, { useState } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import MarketplaceSelector from 'components/coop/MarketplaceSelector.js';

const Overlay = styled.div`${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`}`;
const Modal = styled.div`${tw`bg-white rounded-lg p-8 w-full max-w-2xl max-h-screen overflow-y-auto`}`;
const Title = tw.h2`text-xl font-bold mb-4 text-gray-800`;
const Label = tw.label`block text-sm font-semibold text-gray-600 mb-1`;
const Input = tw.input`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const Select = tw.select`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const Textarea = tw.textarea`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500 h-20`;
const Row = tw.div`flex gap-3 flex-wrap`;
const Col = styled.div`
  flex: 1;
  min-width: 200px;
`;
const ButtonRow = tw.div`flex justify-end gap-3 mt-4`;
const SaveBtn = tw.button`bg-green-500 text-white px-5 py-2 rounded font-semibold hover:bg-green-600 transition`;
const CancelBtn = tw.button`bg-gray-200 text-gray-700 px-5 py-2 rounded font-semibold hover:bg-gray-300 transition`;

export default function AdminCoBuyCreate({ onSave, onCancel }) {
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [neighborhood, setNeighborhood] = useState('Ikeja');
    const [bulkItem, setBulkItem] = useState('Rice Sacks');
    const [targetAmount, setTargetAmount] = useState('');
    const [maxMembers, setMaxMembers] = useState('');
    const [dropoffAddress, setDropoffAddress] = useState('');
    const [expiryHours, setExpiryHours] = useState('72');
    const [marketplaceProducts, setMarketplaceProducts] = useState([]);
    const [marketplaceOpen, setMarketplaceOpen] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const expiryDate = new Date();
            expiryDate.setHours(expiryDate.getHours() + Number(expiryHours));

            const autoCalcTotal = marketplaceProducts.length > 0
                ? marketplaceProducts.reduce((sum, p) => sum + (p.unitPrice * p.quantity), 0)
                : Number(targetAmount);

            const data = {
                name,
                neighborhood,
                bulkItem,
                targetAmount: autoCalcTotal,
                maxMembers: Number(maxMembers),
                dropoffAddress,
                expiryDate: expiryDate.toISOString(),
                currentAmount: 0,
                status: "active",
                createdBy: "admin",
                createdByName: "Admin",
                createdAt: new Date().toISOString(),
                marketplaceProducts,
                members: [],
                messages: [
                    {
                        userId: "system",
                        userName: "System",
                        text: `Co-Op group "${name}" created for ${neighborhood}. Share details and invite neighbors!`,
                        timestamp: new Date().toISOString()
                    }
                ]
            };
            await onSave(data);
        } catch (err) {
            console.error('AdminCoBuyCreate save error:', err);
            alert('Save failed: ' + (err?.message || JSON.stringify(err)));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Overlay onClick={onCancel}>
            <Modal onClick={e => e.stopPropagation()}>
                <Title>Start New Co-Buying Group</Title>
                <form onSubmit={handleSubmit}>
                    <Label>Co-Op Group Name *</Label>
                    <Input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="e.g. Surulere Rice and Grains Pool"
                        required
                    />

                    <Row>
                        <Col>
                            <Label>Neighborhood Area *</Label>
                            <Select
                                value={neighborhood}
                                onChange={e => setNeighborhood(e.target.value)}
                                required
                            >
                                <option value="Ikeja">Ikeja</option>
                                <option value="Lekki">Lekki</option>
                                <option value="Yaba">Yaba</option>
                                <option value="Gbagada">Gbagada</option>
                                <option value="Surulere">Surulere</option>
                            </Select>
                        </Col>
                        <Col>
                            <Label>Bulk Item Pool Category *</Label>
                            <Select
                                value={bulkItem}
                                onChange={e => setBulkItem(e.target.value)}
                                required
                            >
                                <option value="Rice Sacks">Rice Sacks</option>
                                <option value="Beans Sacks">Beans Sacks</option>
                                <option value="Garri Basins">Garri Basins</option>
                                <option value="Vegetable Oil Kegs">Vegetable Oil Kegs</option>
                                <option value="Tomato Baskets">Tomato Baskets</option>
                            </Select>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Label>Total Cost Goal (₦) {marketplaceProducts.length > 0 ? '(Auto-calculated)' : '*'}</Label>
                            <Input
                                type="number"
                                value={marketplaceProducts.length > 0
                                    ? marketplaceProducts.reduce((s, p) => s + p.unitPrice * p.quantity, 0)
                                    : targetAmount
                                }
                                onChange={e => setTargetAmount(e.target.value)}
                                placeholder="e.g. 45000"
                                disabled={marketplaceProducts.length > 0}
                                required={marketplaceProducts.length === 0}
                                style={{ backgroundColor: marketplaceProducts.length > 0 ? '#f3f4f6' : 'white' }}
                            />
                        </Col>
                        <Col>
                            <Label>Member Limit (Optional)</Label>
                            <Input
                                type="number"
                                value={maxMembers}
                                onChange={e => setMaxMembers(e.target.value)}
                                placeholder="Unlimited"
                            />
                        </Col>
                        <Col>
                            <Label>Expiry (Hours, Max 72) *</Label>
                            <Input
                                type="number"
                                value={expiryHours}
                                onChange={e => setExpiryHours(Math.min(72, Math.max(1, e.target.value)))}
                                placeholder="72"
                                min="1"
                                max="72"
                                required
                            />
                        </Col>
                    </Row>

                    <Label>Neighborhood Central Drop-off Site Address *</Label>
                    <Textarea
                        value={dropoffAddress}
                        onChange={e => setDropoffAddress(e.target.value)}
                        placeholder="Specify a coordinator's house or accessible drop-off spot in this area"
                        required
                    />

                    {/* Marketplace product picker */}
                    <div style={{ marginBottom: '1rem' }}>
                        <Label>Marketplace Products (Optional)</Label>
                        <button
                            type="button"
                            onClick={() => setMarketplaceOpen(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', border: '2px dashed #10b981', borderRadius: '8px', background: '#f0fdf4', color: '#065f46', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer', width: '100%', justifyContent: 'center' }}
                        >
                            🛒 Browse &amp; Select from Marketplace
                            {marketplaceProducts.length > 0 && (
                                <span style={{ background: '#10b981', color: 'white', borderRadius: '999px', padding: '1px 8px', fontSize: '0.75rem' }}>
                                    {marketplaceProducts.length} selected
                                </span>
                            )}
                        </button>
                        {marketplaceProducts.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
                                {marketplaceProducts.map(p => (
                                    <span key={p.productId} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#d1fae5', color: '#065f46', fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: '999px' }}>
                                        {p.productName} · {p.quantity} {p.measureUnit}
                                        <button type="button" onClick={() => setMarketplaceProducts(prev => prev.filter(x => x.productId !== p.productId))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#059669', fontWeight: 'bold', fontSize: '0.85rem', lineHeight: 1, padding: 0 }}>×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ backgroundColor: '#fef3c7', padding: '0.75rem', borderRadius: '6px', fontSize: '0.8rem', color: '#92400e', marginBottom: '1rem' }}>
                        <strong>⚠️ Note:</strong> Groups will expire after {expiryHours} hours and won't accept new members after expiry.
                        {marketplaceProducts.length > 0 && (
                            <span> Total cost auto-calculated from {marketplaceProducts.length} selected product(s): <strong>₦{marketplaceProducts.reduce((s, p) => s + p.unitPrice * p.quantity, 0).toLocaleString()}</strong></span>
                        )}
                    </div>

                    <ButtonRow>
                        <CancelBtn type="button" onClick={onCancel}>Cancel</CancelBtn>
                        <SaveBtn type="submit" disabled={saving}>
                            {saving ? 'Creating...' : 'Create Co-Op Group'}
                        </SaveBtn>
                    </ButtonRow>
                </form>
            </Modal>
            {marketplaceOpen && (
                <MarketplaceSelector
                    selectedProducts={marketplaceProducts}
                    onDone={(products) => { setMarketplaceProducts(products); setMarketplaceOpen(false); }}
                    onClose={() => setMarketplaceOpen(false)}
                />
            )}
        </Overlay>
    );
}
